import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSortDown, faSortUp } from "@fortawesome/free-solid-svg-icons";

const ServiceTable = ({
  services,
  sortConfig = { key: null, direction: "asc" },
  onSort = () => {},
}) => {
  const { t } = useTranslation();

  const renderSortableHeader = (label, key) => {
    const isActive = sortConfig.key === key;
    const icon = !isActive
      ? faSortDown
      : sortConfig.direction === "asc"
        ? faSortUp
        : faSortDown;
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
        <span>{label}</span>
        <button
          type="button"
          onClick={() => onSort(key)}
          style={{
            border: "none",
            background: "transparent",
            padding: 0,
            cursor: "pointer",
            color: isActive ? "#1f2937" : "#666",
          }}
          aria-label={`Sort ${label}`}
        >
          <FontAwesomeIcon icon={icon} />
        </button>
      </span>
    );
  };

  return (
    <div className="services-table-wrapper">
      <table className="services-table">
        <thead>
          <tr>
            <th>
              {renderSortableHeader(t("mass_name_label"), "massName")}
            </th>
            <th>{t("region_label")}</th>
            <th>{t("service_name_label")}</th>
            <th>{t("plan_name_label")}</th>
            <th>{t("mass_code_label")}</th>
            <th>{t("status_label")}</th>
            <th>
              {renderSortableHeader(t("order_date_label"), "orderDate")}
            </th>
            <th>
              {renderSortableHeader(t("release_date_label"), "releaseDate")}
            </th>
            <th>{t("end_date_label")}</th>
            <th>{t("invoice_number_label")}</th>
            <th>Minutes Purchased</th>
            <th>Messages Purchased</th>
            <th>Last Minutes Consumed</th>
            <th>Last Messages Consumed</th>
            <th>
              {renderSortableHeader("Last Consumption Date", "lastConsumptionDate")}
            </th>
            <th>Last Consumption %</th>
            <th>Total Consumed Minutes</th>
            <th>Total Consumed Messages</th>
            <th>Total Consumed %</th>
            <th>{renderSortableHeader(t("price_label"), "price")}</th>
            <th>{t("auto_debit_label")}</th>
          </tr>
        </thead>
        <tbody>
          {services && services.length > 0 ? (
            services.map((service, index) => (
              <TableRow key={index} service={service} />
            ))
          ) : (
            <tr>
              <td colSpan="22" className="px-6 py-4 text-center">
                {t("no_data_available")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const TableRow = ({ service }) => {
  const { t } = useTranslation();

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? "" : d.toLocaleDateString();
  };

  const formatDateOrRaw = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleDateString();
  };

  const formatConsumptionPercent = (value) => {
    if (value == null || value === "") return "-";
    const n = Number(value);
    if (!Number.isFinite(n)) return "-";
    const ratio = n > 1 ? n / 100 : n;
    if (ratio < 0) return "0.00%";
    if (ratio > 1) return "100.00%";
    return `${(ratio * 100).toFixed(2)}%`;
  };

  const getTotalConsumedRatio = (row) => {
    const candidates = [
      row?.totalConsumedPercentage,
      row?.totalConsumedPercent,
      row?.totalConsumption,
      row?.totalConsumptionRatio,
      row?.overallConsumption,
    ];
    for (const value of candidates) {
      if (value !== undefined && value !== null && value !== "") {
        return value;
      }
    }
    return null;
  };

  const formatNumber = (value) => {
    if (value == null || value === "") return "-";
    const n = Number(value);
    if (!Number.isFinite(n)) return "-";
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
  };

  return (
    <tr>
      <td data-label={t("mass_name_label")}>{service.massName ?? "-"}</td>
      <td data-label={t("region_label")}>{service.region ?? "-"}</td>
      <td data-label={t("service_name_label")}>{service.serviceName ?? "-"}</td>
      <td data-label={t("plan_name_label")}>{service.planName ?? "-"}</td>
      <td data-label={t("mass_code_label")}>{service.massCode ?? "-"}</td>
      <td data-label={t("status_label")}>{service.status ?? "-"}</td>
      <td data-label={t("order_date_label")}>{formatDate(service.orderDate)}</td>
      <td data-label={t("release_date_label")}>{formatDate(service.releaseDate)}</td>
      <td data-label={t("end_date_label")}>{formatDate(service.endDate)}</td>
      <td data-label={t("invoice_number_label")}>{service.invoiceNumber || "-"}</td>
      <td data-label="Minutes Purchased">{formatNumber(service.minutesPurchased)}</td>
      <td data-label="Messages Purchased">{formatNumber(service.messagesPurchased)}</td>
      <td data-label="Last Minutes Consumed">{formatNumber(service.consumedMinutes)}</td>
      <td data-label="Last Messages Consumed">{formatNumber(service.consumedMessages)}</td>
      <td data-label="Last Consumption Date">{formatDateOrRaw(service.lastConsumptionDate)}</td>
      <td data-label="Last Consumption %">{formatConsumptionPercent(service.consumption)}</td>
      <td data-label="Total Consumed Minutes">{formatNumber(service.totalConsumedMinutes)}</td>
      <td data-label="Total Consumed Messages">{formatNumber(service.totalConsumedMessages)}</td>
      <td data-label="Total Consumed %">{formatConsumptionPercent(getTotalConsumedRatio(service))}</td>
      <td data-label={t("price_label")}>
        {service.price !== undefined && service.price !== null && !isNaN(service.price)
          ? Number(service.price).toFixed(2)
          : "-"}
      </td>
      <td data-label={t("auto_debit_label")}>
        {service.autoDebit
          ? ["yes", "true"].includes(service.autoDebit.toLowerCase())
            ? t("yes")
            : t("no")
          : "-"}
      </td>
    </tr>
  );
};

export default ServiceTable;
